import { all, call, fork, put, select, take } from 'redux-saga/effects';

import { getAllInstruments } from '!core/redux/userInstruments/userInstruments.selectors';
import { securityAccountSelectedSelector } from '!core/redux/userSecuritiesAccounts/userSecuritiesAccounts.selectors';
import { publicOfferServices } from '!views/Market/redux/publicOffer.services';
import { webAreasServices } from '!views/Market/redux/webAreas/webAreas.services';
import {
  getCurrentStockExchangeTab,
  getWebAreas,
} from '!views/Market/StockExchange/marketStockExchange.seletors';
import { handleSagaError } from '»utils/handleSagasError';
import { strBeforeLastOccurrence } from '»utils/string';

import { tradingServices } from './trading.services';
import { tradingSlice } from './trading.slice';

const tradingDailyWorker = function* () {
  try {
    const securityAccount = yield select(securityAccountSelectedSelector);

    const loadBookData = yield call(tradingServices.loadBook, {
      clientId: securityAccount,
    });

    const publicOffersAll = yield call(publicOfferServices.publicOfferAll, {
      account: securityAccount,
    });

    const bookDaily = loadBookData.reduce((acc, el) => {
      const match = publicOffersAll.find(
        (obj) =>
          obj.securityType === el.securityType &&
          obj.symbol === el.symbol &&
          obj.securityExchange === el.securityExchange,
      );

      if (match)
        acc.push({
          ...el,
          orderPrice: el.price,
          customSymbol: strBeforeLastOccurrence(el.symbol, '.'),
          priceType: match.priceType,
          bodivaCode: match.bodivaCode,
          clientType: match.clientType,
          endDate: match.endDate,
          exchangeId: match.exchangeId,
          instrumentId: match.instrumentId,
          maxPrice: match.maxPrice,
          maxQuantity: match.maxQuantity,
          minimumBatch: match.minimumBatch,
          minNbrBatches: match.minNbrBatches,
          minPrice: match.minPrice,
          nominalValue: match.nominalValue,
          noteId: match.noteId,
          offerMaxPrice: match.offerMaxPrice,
          offerMinPrice: match.offerMinPrice,
          offerType: match.offerType,
          operationDate: match.operationDate,
          publicOfferDesc: match.publicOfferDesc,
          publicOfferId: match.publicOfferId,
          quantityType: match.quantityType,
          revokeDate: match.revokeDate,
          settlementDate: match.settlementDate,
          startDate: match.startDate,
          state: match.state,
          subscriptionDate: match.subscriptionDate,
          trancheDescr: match.trancheDescr,
          trancheId: match.trancheId,
          trancheType: match.trancheType,
          validateBalance: match.validateBalance,
          visibleWeb: match.visibleWeb,
          price: match.price,
          maxAmount: match.maxAmount,
          minAmount: match.minAmount,
          minQuantity: match.minQuantity,
          typeNegotiation: match.typeNegotiation,
        });

      return acc;
    }, []);

    yield put(tradingSlice.actions.tradingDailySucess(bookDaily));
  } catch (error) {
    const handled = yield call(handleSagaError, error);
    if (!handled) yield put(tradingSlice.actions.tradingDailyFailure());
  }
};

const tradingDailySagas = function* () {
  while (true) {
    const authorized = false;
    try {
      while (!authorized) {
        yield take(tradingSlice.actions.tradingDailyRequest.type);
        yield call(tradingDailyWorker);
      }
    } catch {
      yield put(tradingSlice.actions.tradingDailyFailure());
    }
  }
};

const tradingHstWorker = function* (endDate) {
  try {
    const securityAccount = yield select(securityAccountSelectedSelector);

    const loadHstBookData = yield call(tradingServices.loadHstBook, {
      clientId: securityAccount,
      endDate,
    });
    const publicOffersAll = yield call(publicOfferServices.publicOfferAll, {
      account: securityAccount,
    });

    const bookHst = loadHstBookData.map((el) => {
      const match = publicOffersAll.find(
        (obj) =>
          obj.securityType === el.securityType &&
          obj.symbol === el.symbol &&
          obj.securityExchange === el.securityExchange,
      );

      if (!match) {
        return el;
      }

      return {
        ...el,
        orderPrice: el.price,
        customSymbol: strBeforeLastOccurrence(el.symbol, '.'),
        priceType: match.priceType,
        bodivaCode: match.bodivaCode,
        clientType: match.clientType,
        endDate: match.endDate,
        exchangeId: match.exchangeId,
        instrumentId: match.instrumentId,
        maxPrice: match.maxPrice,
        maxQuantity: match.maxQuantity,
        minimumBatch: match.minimumBatch,
        minNbrBatches: match.minNbrBatches,
        minPrice: match.minPrice,
        nominalValue: match.nominalValue,
        noteId: match.noteId,
        offerMaxPrice: match.offerMaxPrice,
        offerMinPrice: match.offerMinPrice,
        offerType: match.offerType,
        operationDate: match.operationDate,
        publicOfferDesc: match.publicOfferDesc,
        publicOfferId: match.publicOfferId,
        quantityType: match.quantityType,
        revokeDate: match.revokeDate,
        settlementDate: match.settlementDate,
        startDate: match.startDate,
        state: match.state,
        subscriptionDate: match.subscriptionDate,
        trancheDescr: match.trancheDescr,
        trancheId: match.trancheId,
        trancheType: match.trancheType,
        validateBalance: match.validateBalance,
        visibleWeb: match.visibleWeb,
        price: match.price,
        maxAmount: match.maxAmount,
        minAmount: match.minAmount,
        minQuantity: match.minQuantity,
      };
    });

    yield put(tradingSlice.actions.tradingHstSucess(bookHst));
  } catch (error) {
    const handled = yield call(handleSagaError, error);
    if (!handled) yield put(tradingSlice.actions.tradingHstFailure());
  }
};

const tradingHstSagas = function* () {
  while (true) {
    const authorized = false;
    try {
      while (!authorized) {
        const action = yield take(tradingSlice.actions.tradingHstRequest.type);
        const { endDate } = action.payload;

        yield call(tradingHstWorker, endDate);
      }
    } catch {
      yield put(tradingSlice.actions.tradingHstFailure());
    }
  }
};

const tradingDailyStockExchangeWorker = function* ({ getEvents }) {
  try {
    const securityAccount = yield select(securityAccountSelectedSelector);
    const webAreas = yield select(getWebAreas);
    const currentWebAreaTab = yield select(getCurrentStockExchangeTab);
    const webAreaId = webAreas[currentWebAreaTab].wearseq;

    const loadBookStockExchangeData = yield call(
      tradingServices.loadBookStockExchange,
      {
        clientId: securityAccount,
      },
    );

    const marketStockExchange = yield call(
      webAreasServices.webAreaInstrQuotes,
      {
        account: securityAccount,
        webPage: 'QUOTES_MARKET',
        webArea: webAreaId,
      },
    );

    const respElementIndex = marketStockExchange.findIndex(
      (el) => el.type === 'resp',
    );

    marketStockExchange.splice(respElementIndex, respElementIndex >= 0 ? 1 : 0);

    const userInstrumentsData = yield select(getAllInstruments);

    const mergedData = mergeData(
      loadBookStockExchangeData,
      userInstrumentsData,
      marketStockExchange,
    );

    yield put(
      tradingSlice.actions.tradingDailyStockExchangeSuccess(mergedData),
    );
    if (getEvents && mergedData.length > 0)
      yield put(
        tradingSlice.actions.tradeEventsRequest({
          ordBookKey: mergedData[0].ordBookKey,
          bookType: 'B',
          market: 'stockExchange',
        }),
      );
  } catch (error) {
    const handled = yield call(handleSagaError, error);
    if (!handled)
      yield put(tradingSlice.actions.tradingDailyStockExchangeFailure());
  }
};

const tradingDailyFundsWorker = function* ({ getEvents }) {
  try {
    const securityAccount = yield select(securityAccountSelectedSelector);

    const loadBookFundsPrimaryData = yield call(
      tradingServices.loadBookPrimaryPrivate,
      {
        clientId: securityAccount,
      },
    );

    const loadBookFundsManagerData = yield call(tradingServices.loadBookFund, {
      clientId: securityAccount,
    });

    const loadBookFundsData = loadBookFundsPrimaryData.concat(
      loadBookFundsManagerData,
    );

    const userInstrumentsData = yield select(getAllInstruments);

    const mergedDataFunds = mergeData(loadBookFundsData, userInstrumentsData);

    mergedDataFunds.sort((a, b) => new Date(b.ordDate) - new Date(a.ordDate));

    yield put(tradingSlice.actions.tradingDailyFundsSucess(mergedDataFunds));
    if (getEvents && mergedDataFunds.length > 0)
      yield put(
        tradingSlice.actions.tradeEventsRequest({
          ordBookKey: mergedDataFunds[0].ordBookKey,
          bookType: 'B',
          market: 'funds',
        }),
      );
  } catch (error) {
    const handled = yield call(handleSagaError, error);
    if (!handled) yield put(tradingSlice.actions.tradingDailyFundsFailure());
  }
};

const tradingDailyFundsSagas = function* () {
  while (true) {
    const authorized = false;
    try {
      while (!authorized) {
        const action = yield take(
          tradingSlice.actions.tradingFundsDailyRequest.type,
        );
        const { getEvents } = action.payload;
        yield call(tradingDailyFundsWorker, { getEvents });
      }
    } catch {
      yield put(tradingSlice.actions.tradingDailyStockExchangeFailure());
    }
  }
};

const tradingHstStockExchangeWorker = function* ({ endDate, getEvents }) {
  try {
    const securityAccount = yield select(securityAccountSelectedSelector);

    const loadHstBookStockExchangeData = yield call(
      tradingServices.loadHstBookStockExchange,
      {
        clientId: securityAccount,
        endDate,
      },
    );

    const userInstrumentsData = yield select(getAllInstruments);

    const mergedData = mergeData(
      loadHstBookStockExchangeData,
      userInstrumentsData,
    );

    yield put(tradingSlice.actions.tradingHstStockExchangeSucess(mergedData));
    if (getEvents && mergedData.length > 0)
      yield put(
        tradingSlice.actions.tradeEventsRequest({
          ordBookKey: mergedData[0].ordBookKey,
          bookType: 'H',
          market: 'stockExchange',
        }),
      );
  } catch (error) {
    const handled = yield call(handleSagaError, error);
    if (!handled)
      yield put(tradingSlice.actions.tradingHstStockExchangeFailure());
  }
};

const tradingHstStockExchangeSagas = function* () {
  while (true) {
    const authorized = false;
    try {
      while (!authorized) {
        const action = yield take(
          tradingSlice.actions.tradingHstStockExchangeRequest.type,
        );
        const { endDate, getEvents } = action.payload;

        yield call(tradingHstStockExchangeWorker, { endDate, getEvents });
      }
    } catch {
      yield put(tradingSlice.actions.tradingHstStockExchangeFailure());
    }
  }
};

const tradingDailyStockExchangeSagas = function* () {
  while (true) {
    const authorized = false;
    try {
      while (!authorized) {
        const action = yield take(
          tradingSlice.actions.tradingDailyStockExchangeRequest.type,
        );
        const { getEvents } = action.payload;

        yield call(tradingDailyStockExchangeWorker, { getEvents });
      }
    } catch {
      yield put(tradingSlice.actions.tradingDailyStockExchangeFailure());
    }
  }
};

const tradingHstFundsWorker = function* ({ endDate, getEvents }) {
  try {
    const securityAccount = yield select(securityAccountSelectedSelector);

    const loadHstBookFundsPrimaryData = yield call(
      tradingServices.loadHstBookPrimaryPrivate,
      {
        clientId: securityAccount,
        endDate,
      },
    );

    const loadHstBookFundsManagerData = yield call(
      tradingServices.loadHstBookFund,
      {
        clientId: securityAccount,
        endDate,
      },
    );

    const loadHstBookFundsData = loadHstBookFundsPrimaryData.concat(
      loadHstBookFundsManagerData,
    );

    const userInstrumentsData = yield select(getAllInstruments);

    const mergedDataFunds = mergeData(
      loadHstBookFundsData,
      userInstrumentsData,
    );

    mergedDataFunds.sort((a, b) => new Date(b.ordDate) - new Date(a.ordDate));

    yield put(tradingSlice.actions.tradingHstFundsSucess(mergedDataFunds));
    if (getEvents && mergedDataFunds.length > 0)
      yield put(
        tradingSlice.actions.tradeEventsRequest({
          ordBookKey: mergedDataFunds[0].ordBookKey,
          bookType: 'H',
          market: 'funds',
        }),
      );
  } catch (error) {
    const handled = yield call(handleSagaError, error);
    if (!handled) yield put(tradingSlice.actions.tradingHstFundsFailure());
  }
};

const tradingHstFundsSagas = function* () {
  while (true) {
    const authorized = false;
    try {
      while (!authorized) {
        const action = yield take(
          tradingSlice.actions.tradingHstFundsRequest.type,
        );
        const { endDate, getEvents } = action.payload;

        yield call(tradingHstFundsWorker, { endDate, getEvents });
      }
    } catch {
      yield put(tradingSlice.actions.tradingHstStockExchangeFailure());
    }
  }
};

const joinOrdData = (ordData, ordExtraData) => {
  const ordDataExchange = ordData.securityExchange ?? ordData.exchange;
  const ordExtraDataExchange =
    ordExtraData.securityExchange ?? ordExtraData.exchange;

  return (
    ordExtraData.securityType === ordData.securityType &&
    ordExtraData.symbol === ordData.symbol &&
    ordExtraDataExchange === ordDataExchange
  );
};

const mergeData = (currentData, newData, extraData = []) => {
  const completedData = [];
  currentData.forEach((currentField) => {
    const newField = newData.find((field) => joinOrdData(currentField, field));

    const extraField =
      extraData.length &&
      extraData.find((field) => joinOrdData(currentField, field));

    completedData.push({ ...newField, ...extraField, ...currentField });
  });
  return completedData;
};

const tradeEventsWorker = function* ({ ordBookKey, bookType }) {
  try {
    const securityAccount = yield select(securityAccountSelectedSelector);

    const bookEvents = yield call(tradingServices.loadBookEvents, {
      clientId: securityAccount,
      ordBookKey,
      bookType,
    });

    const lastBookEvent = bookEvents.reduce((acc, currentEvent, index) => {
      if (index === 0 || Number(currentEvent.id) > Number(acc.id))
        return currentEvent;

      return acc;
    }, null);

    yield put(
      tradingSlice.actions.tradeEventsSucess({
        ordBookKey,
        timestamp: lastBookEvent?.timestamp,
        events: bookEvents,
        bookType,
      }),
    );
  } catch (error) {
    const handled = yield call(handleSagaError, error);
    if (!handled) yield put(tradingSlice.actions.tradeEventsFailure());
  }
};

const tradeEventsSagas = function* () {
  while (true) {
    const authorized = false;
    try {
      while (!authorized) {
        const action = yield take(tradingSlice.actions.tradeEventsRequest.type);
        const { ordBookKey, bookType, market } = action.payload;

        yield call(tradeEventsWorker, { ordBookKey, bookType, market });
      }
    } catch {
      yield put(tradingSlice.actions.tradeEventsFailure());
    }
  }
};

const loadDocListWorker = function* (ordBookKey) {
  try {
    const loadDocList = yield call(tradingServices.loadDocList, {
      ordBookKey,
    });

    yield put(
      tradingSlice.actions.loadDocListSuccess({
        docs: loadDocList,
      }),
    );
  } catch (error) {
    const handled = yield call(handleSagaError, error);
    if (!handled) yield put(tradingSlice.actions.loadDocListFailure());
  }
};

const loadDocListSagas = function* () {
  while (true) {
    const authorized = false;
    try {
      while (!authorized) {
        const action = yield take(tradingSlice.actions.loadDocListRequest.type);
        const { ordBookKey } = action.payload;

        yield call(loadDocListWorker, ordBookKey);
      }
    } catch {
      yield put(tradingSlice.actions.loadDocListFailure());
    }
  }
};

export const tradingSagas = function* () {
  try {
    yield all([
      fork(tradingDailySagas),
      fork(tradingHstSagas),
      fork(tradingDailyStockExchangeSagas),
      fork(tradingHstStockExchangeSagas),
      fork(tradeEventsSagas),
      fork(loadDocListSagas),
      fork(tradingDailyFundsSagas),
      fork(tradingHstFundsSagas),
    ]);
  } catch {}
};
